import {
  PAGE_BOUNDED_ATTR,
  SHELL_CARD_BOUNDED,
  SHELL_CARD_UNBOUNDED,
  SHELL_FLUSH_CHROME,
} from "./shell";
import { PAGE_HIGHLIGHT_SURFACE_CLASS } from "./surface";
import { cn } from "@/lib/utils";
import {
  type ComponentPropsWithRef,
  type CSSProperties,
  type ReactNode,
} from "react";

export const PAGE_CARD_SCROLL_CONTAINER_ATTR = "data-page-card-scroll";

const SCROLLABLE_CLASSES =
  "overflow-y-auto overscroll-none [scrollbar-width:thin] [scrollbar-color:hsl(var(--border))_transparent] [&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-clip-padding [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-transparent";

export type PageCardProps = ComponentPropsWithRef<"main"> & {
  children: ReactNode;
  style?: CSSProperties;
  surface?: "primary" | "highlight";
  scrollable?: boolean;
  inset?: boolean;
  // The page scrolls inside this card; inside the shell the card takes the
  // viewport height and the inset stops scrolling the document.
  bounded?: boolean;
};

const PageCardRoot = ({
  children,
  id,
  className,
  style,
  surface = "primary",
  scrollable = true,
  inset = false,
  bounded = false,
  ...props
}: PageCardProps) => {
  const pageCardSurface =
    surface === "primary"
      ? "hsl(var(--surface-primary))"
      : "hsl(var(--surface-highlight))";

  return (
    <main
      id={id}
      style={
        {
          "--page-card-surface": pageCardSurface,
          ...style,
        } as CSSProperties
      }
      {...props}
      {...(scrollable ? { [PAGE_CARD_SCROLL_CONTAINER_ATTR]: "" } : {})}
      {...(bounded ? { [PAGE_BOUNDED_ATTR]: "" } : {})}
      className={cn(
        "relative flex min-h-0 min-w-0 flex-1 flex-col rounded-lg border border-surface-quatenary",
        inset && "sm:m-1",
        surface === "highlight" && PAGE_HIGHLIGHT_SURFACE_CLASS,
        "bg-[var(--page-card-surface)]",
        // clip, not hidden: hidden makes the card a scrollport, which would
        // pin the header's sticky to a box that never scrolls.
        scrollable ? SCROLLABLE_CLASSES : "overflow-clip",
        className,
        SHELL_FLUSH_CHROME,
        bounded ? SHELL_CARD_BOUNDED : SHELL_CARD_UNBOUNDED
      )}
    >
      {children}
    </main>
  );
};

const PageCardHeader = ({
  className,
  ...props
}: ComponentPropsWithRef<"header">) => (
  <header
    className={cn(
      // A phone's mark and sheet trigger are fixed, so the row that carries
      // the crumbs between them stays put as a tall page scrolls.
      "relative flex h-12 shrink-0 items-center gap-2 bg-[var(--page-card-surface)] px-2 shadow-[inset_0_-1px_0_0_hsl(var(--surface-quatenary))] max-md:sticky max-md:top-0 max-md:z-30",
      className
    )}
    {...props}
  />
);
PageCardHeader.displayName = "PageCard.Header";

const PageCardContent = ({
  className,
  ...props
}: ComponentPropsWithRef<"div">) => (
  <div
    {...{ [PAGE_CARD_SCROLL_CONTAINER_ATTR]: "" }}
    className={cn(
      "min-h-0 flex-1 bg-[var(--page-card-surface)]",
      SCROLLABLE_CLASSES,
      className
    )}
    {...props}
  />
);
PageCardContent.displayName = "PageCard.Content";

export const PageCard = Object.assign(PageCardRoot, {
  Header: PageCardHeader,
  Content: PageCardContent,
});
